import { z } from "zod";

import type { ApplyFailureReason } from "@common/constants/appliedActions";

import { excerptOf } from "./text-excerpt";

export class ErrorWithCause extends Error {
    constructor(message: string, cause?: Error) {
        super(message, { cause });
    }
}

export class UserFacingError extends ErrorWithCause {
    status?: number;

    get userMessage(): string {
        return this.message;
    }
}

export const WORD_BUSY_MESSAGE = "Word didn't respond to our request in time. Please try again in a moment.";

const WORD_BUSY_REFUSAL_TEXT = "wait until the previous call completes";

export function isWordBusyRefusal(error: unknown): boolean {
    const errorsSeen = new Set<unknown>();
    let current: unknown = error;
    while (current instanceof Error && !errorsSeen.has(current)) {
        if (current.message.toLowerCase().includes(WORD_BUSY_REFUSAL_TEXT)) return true;
        errorsSeen.add(current);
        current = current.cause;
    }
    return false;
}

export function userMessageOf(error: unknown): string {
    if (error instanceof UserFacingError) return error.userMessage;
    if (isWordBusyRefusal(error)) return WORD_BUSY_MESSAGE;
    if (error instanceof Error) return error.message;
    return String(error);
}

export class PartialRedlineApply extends UserFacingError {
    readonly everyWriteLanded: boolean = false;

    constructor(readonly landed: number, readonly total: number, cause?: Error) {
        super(
            landed > 0
                ? `Partly applied — ${landed} of ${total} changes reached the document. Regenerate this redline to apply the rest; applying again would duplicate what already landed.`
                : `This may have changed part of the document before it failed. Regenerate this redline rather than applying it again, which could duplicate those changes.`,
            cause,
        );
        this.name = "PartialRedlineApply";
    }
}

const PARAGRAPH_LABEL_CHARACTERS = 40;

export const quotedParagraphLabelOf = (paragraphText: string): string => `“${excerptOf(paragraphText, PARAGRAPH_LABEL_CHARACTERS)}”`;

const quotedParagraphLabelsOf = (paragraphTexts: string[]): string => paragraphTexts.map(quotedParagraphLabelOf).join(", ");

export const writtenBackReasonOf = (deletedParagraphTexts: string[]): string =>
    deletedParagraphTexts.length === 1
        ? `${quotedParagraphLabelsOf(deletedParagraphTexts)} was deleted from the document; this change wrote it back as a new paragraph you can reject.`
        : `${quotedParagraphLabelsOf(deletedParagraphTexts)} were deleted from the document; this change wrote them back as new paragraphs you can reject.`;

export const cellsNotWrittenReasonOf = (goneCellLabels: string[], unreturnedCellLabels: string[]): string => [
    ...(goneCellLabels.length === 0 ? [] : [goneCellLabels.length === 1
        ? `The row ${quotedParagraphLabelsOf(goneCellLabels)} is no longer in the document, so its change was not written.`
        : `The rows ${quotedParagraphLabelsOf(goneCellLabels)} are no longer in the document, so their changes were not written.`]),
    ...(unreturnedCellLabels.length === 0 ? [] : [`Word did not return ${quotedParagraphLabelsOf(unreturnedCellLabels)} when the changes were applied together, so this change did not write ${unreturnedCellLabels.length === 1 ? "it" : "them"}.`]),
].join(" ");

export class CellsNotWrittenRedlineApply extends PartialRedlineApply {
    constructor(goneCellLabels: string[], unreturnedCellLabels: string[], landedCellCount: number, changedCellCount: number) {
        super(landedCellCount, changedCellCount);
        this.message = cellsNotWrittenReasonOf(goneCellLabels, unreturnedCellLabels);
        this.name = "CellsNotWrittenRedlineApply";
    }
}

export interface CommentNotWritten {
    commentText: string;
    paragraphGone: boolean;
}

export const commentNotWrittenCauseOf = (paragraphGone: boolean): string => (paragraphGone ? "its paragraph is not in the document" : "Word refused it");

const commentNotWrittenReasonOf = (commentNotWritten: CommentNotWritten): string =>
    `Comment ${quotedParagraphLabelOf(commentNotWritten.commentText)} not added: ${commentNotWrittenCauseOf(commentNotWritten.paragraphGone)}.`;

export class CommentsNotWrittenRedlineApply extends PartialRedlineApply {
    readonly everyWriteLanded: boolean = true;

    constructor(readonly commentsNotWritten: CommentNotWritten[], commentCount: number) {
        super(commentCount - commentsNotWritten.length, commentCount);
        this.message = commentsNotWritten.map(commentNotWrittenReasonOf).join(" ");
        this.name = "CommentsNotWrittenRedlineApply";
    }
}

export class WrittenBackRedlineApply extends PartialRedlineApply {
    readonly everyWriteLanded: boolean = true;

    constructor(readonly deletedParagraphTexts: string[]) {
        super(deletedParagraphTexts.length, deletedParagraphTexts.length);
        this.message = writtenBackReasonOf(deletedParagraphTexts);
        this.name = "WrittenBackRedlineApply";
    }
}

export const CANNOT_LOCATE_TARGET_MESSAGE =
    "We cannot find where to apply this. Changes may have altered the location after it was generated. Try regenerating.";

export class ApplyFailureError extends UserFacingError {
    reason: ApplyFailureReason;

    constructor(message: string, reason: ApplyFailureReason, cause?: Error) {
        super(message, cause);
        this.reason = reason;
    }
}

/**
 * Normalize any thrown/caught value into an Error with a human-readable message.
 * Errors pass through untouched (preserving Office.js/OfficeExtension subclasses).
 * JSON-shaped strings and plain objects are pretty-printed so the message reads
 * cleanly instead of "[object Object]"; anything unserializable falls back to String().
 */
export function toError(value: unknown): Error {
    if (value instanceof Error) return value;
    if (typeof value === "string") {
        try {
            return new Error(JSON.stringify(JSON.parse(value), null, 2));
        } catch {
            return new Error(value);
        }
    }
    try {
        return new Error(JSON.stringify(value, null, 2));
    } catch {
        return new Error(String(value));
    }
}

export interface WordErrorDetail {
    name: string;
    code: string;
    message: string;
    location: string | null;
}

const WORD_ERROR_SHAPE = z.object({
    name: z.string(),
    code: z.string(),
    message: z.string(),
    debugInfo: z.object({ errorLocation: z.string() }).partial().optional(),
});

export function wordErrorDetailOf(error: unknown): WordErrorDetail | null {
    const parsed = WORD_ERROR_SHAPE.safeParse(error);
    if (!parsed.success) return null;
    return {
        name: parsed.data.name,
        code: parsed.data.code,
        message: parsed.data.message,
        location: parsed.data.debugInfo?.errorLocation ?? null,
    };
}

const NETWORK_ERROR_PATTERN = /network error|failed to fetch|load failed|network request failed|networkerror/i;

export function isNetworkError(err: unknown): boolean {
    if (!(err instanceof Error)) return false;
    if (err.name === "AbortError") return true;
    if (err instanceof TypeError && NETWORK_ERROR_PATTERN.test(err.message)) return true;
    return false;
}
